
import { useInView } from "react-intersection-observer";
import { useEffect, memo, useCallback } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import EmailListItem from "./EmailListItem";
import { Email } from "@/types/email";

interface EmailListScrollProps {
  emails: Email[];
  selectedEmailIds: Set<string>;
  onSelect: (email: Email, checked: boolean) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

// Use memo to prevent re-renders when props haven't changed
export const EmailListScroll = memo(({
  emails,
  selectedEmailIds,
  onSelect,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: EmailListScrollProps) => {
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.5,
    delay: 100 // Add a small delay to reduce the frequency of checks
  });

  // Only fetch more emails when actually needed
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Memoize the selection handler for each email item
  const handleSelect = useCallback((email: Email, checked: boolean) => {
    onSelect(email, checked);
  }, [onSelect]);

  return (
    <ScrollArea className="h-[calc(100vh-8rem)]">
      <div className="space-y-1">
        {emails.map((email) => (
          <EmailListItem
            key={email.id}
            email={email}
            isSelected={selectedEmailIds.has(email.id)}
            onSelect={handleSelect}
          />
        ))}
        {hasNextPage && (
          <div ref={loadMoreRef} className="py-4 text-center text-sm text-muted-foreground">
            {isFetchingNextPage ? "Loading more..." : "Scroll to load more"}
          </div>
        )}
      </div>
    </ScrollArea>
  );
});

EmailListScroll.displayName = "EmailListScroll";
